class SizeGuide extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.sectionId = this.dataset.sectionId || ''
    this.conversion = this.normaliseConversion(this.dataset.conversion)
    this.headings = this.dataset.headings || 'UK'
    this.bodyRegions = this.getConfiguredBodyRegions()
    this.bodyRegion = this.getMatchingBodyRegion(this.dataset.bodyRegion) || 'UK'
    this.showConverter = this.dataset.showConverter === 'true'
    this.fittingInfo = this.dataset.fittingInfo || ''

    this.onFitUpdate = this.onFitUpdate.bind(this)
    document.addEventListener('size-guide:fit-update', this.onFitUpdate)

    this.garmentTable = this.getTable('garment')
    this.bodyTable = this.getTable('body')

    if (this.showConverter) {
      this.handleInitialConversion()
    }

    this.handleInitialHeadings()
    this.syncMeasurementsToggle()
    this.applyMeasurementsVisibility(this.conversion)
    this.syncBodyRegionSelect()
    this.applyBodyRegionVisibility(this.bodyRegion)
    this.setInitialTab()
    this.updateFittingUI(this.fittingInfo)
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    document.removeEventListener('size-guide:fit-update', this.onFitUpdate)
  }

  setupListeners () {
    super.setupListeners()

    if (this.els?.trigger?.exists) {
      this.handleTrigger = this.handleTrigger.bind(this)
      this.els.trigger.elements.forEach((el) => {
        this.listeners.add(el, 'click', this.handleTrigger)
      })
    }

    if (this.els?.inches?.exists) {
      this.onInchesClick = this.onInchesClick.bind(this)
      this.els.inches.elements.forEach((el) => {
        this.listeners.add(el, 'click', this.onInchesClick)
      })
    }

    if (this.els?.centimetres?.exists) {
      this.onCentimetresClick = this.onCentimetresClick.bind(this)
      this.els.centimetres.elements.forEach((el) => {
        this.listeners.add(el, 'click', this.onCentimetresClick)
      })
    }

    if (this.els?.select?.exists) {
      this.onSelectChange = this.onSelectChange.bind(this)
      this.els.select.elements.forEach((el) => {
        this.listeners.add(el, 'change', this.onSelectChange)
      })
    }

    if (this.els?.measurementsToggleWrapper?.exists && this.els?.measurementsToggleInput?.exists) {
      this.onMeasurementsToggleWrapperClick = this.onMeasurementsToggleWrapperClick.bind(this)
      this.els.measurementsToggleWrapper.elements.forEach((el) => {
        this.listeners.add(el, 'click', this.onMeasurementsToggleWrapperClick)
      })
    }

    if (this.els?.measurementsToggleInput?.exists) {
      this.onMeasurementsInputChange = this.onMeasurementsInputChange.bind(this)
      this.els.measurementsToggleInput.elements.forEach((el) => {
        this.listeners.add(el, 'change', this.onMeasurementsInputChange)
      })
    }

    if (this.els?.regionSelect?.exists) {
      this.onBodyRegionSelectChange = this.onBodyRegionSelectChange.bind(this)
      this.els.regionSelect.elements.forEach((el) => {
        this.listeners.add(el, 'change', this.onBodyRegionSelectChange)
      })
    }

    this.onTableCellMouseEnter = this.onTableCellMouseEnter.bind(this)
    this.onTableMouseLeave = this.onTableMouseLeave.bind(this)

    if (this.els?.table?.exists) {
      this.els.table.elements.forEach((table) => {
        this.listeners.add(table, 'mouseleave', this.onTableMouseLeave)

        table.querySelectorAll('th, td').forEach((cell) => {
          this.listeners.add(cell, 'mouseenter', this.onTableCellMouseEnter)
        })
      })
    }
  }

  syncMeasurementsToggle () {
    if (!this.els?.measurementsToggleInput?.exists) return

    this.els.measurementsToggleInput.elements.forEach((input) => {
      input.checked = this.isInchesConversion(this.conversion)
    })
  }

  async onMeasurementsToggleWrapperClick (event) {
    const inputEl = this.els.measurementsToggleInput.element

    if (event.target === inputEl) return
    if (this.els?.measurementsToggleSwitch?.exists && event.target.closest(this.els.measurementsToggleSwitch.selector)) {
      return
    }

    inputEl.checked = !inputEl.checked
    await this.updateMeasurementsConversion(inputEl.checked ? 'inches' : 'centimetres')
  }

  async onMeasurementsInputChange (event) {
    const isChecked = event.target.checked
    await this.updateMeasurementsConversion(isChecked ? 'inches' : 'centimetres')
  }

  async updateMeasurementsConversion (nextConversion) {
    const normalizedConversion = this.normaliseConversion(nextConversion)

    this.conversion = normalizedConversion
    this.syncMeasurementsToggle()
    this.applyMeasurementsVisibility(normalizedConversion)

    await this.persistConversion(normalizedConversion)
  }

  applyMeasurementsVisibility (conversion) {
    const nextUnit = this.getMeasurementsUnitFromConversion(conversion)

    if (!this.els?.measurementsTable?.exists) return

    this.els.measurementsTable.elements.forEach((tableEl) => {
      tableEl.setAttribute('aria-hidden', tableEl.dataset.unit !== nextUnit)
    })
  }

  async onBodyRegionSelectChange (event) {
    const nextRegion = this.getMatchingBodyRegion(event?.target?.value)
    if (!nextRegion) return

    this.bodyRegion = nextRegion
    this.syncBodyRegionSelect()
    this.applyBodyRegionVisibility(nextRegion)

    await this.persistBodyRegion(nextRegion)
  }

  syncBodyRegionSelect () {
    if (!this.els?.regionSelect?.exists) return

    const activeRegionHandle = this.toHandle(this.bodyRegion)

    this.els.regionSelect.elements.forEach((selectEl) => {
      const match = Array.from(selectEl.options).find((option) => this.toHandle(option.value) === activeRegionHandle)
      if (match) {
        selectEl.value = match.value
      }
    })
  }

  applyBodyRegionVisibility (region) {
    if (!this.els?.measurementsTable?.exists) return

    const activeRegionHandle = this.toHandle(region)
    const configuredRegionHandles = this.bodyRegions.map((value) => this.toHandle(value)).filter(Boolean)
    if (!activeRegionHandle || configuredRegionHandles.length === 0) return

    this.els.measurementsTable.elements.forEach((tableEl) => {
      const rows = Array.from(tableEl.querySelectorAll('tr'))
      if (rows.length === 0) return

      const headerCells = Array.from(rows[0].querySelectorAll('th, td'))
      const regionIndexes = []
      let activeRegionIndex = -1

      headerCells.forEach((cell, index) => {
        const headerHandle = this.toHandle(cell.textContent)
        if (!configuredRegionHandles.includes(headerHandle)) return

        regionIndexes.push(index)
        if (headerHandle === activeRegionHandle) {
          activeRegionIndex = index
        }
      })

      if (regionIndexes.length === 0) return
      if (activeRegionIndex === -1) {
        activeRegionIndex = regionIndexes[0]
      }

      const regionIndexSet = new Set(regionIndexes)

      headerCells.forEach((cell, index) => {
        const shouldHide = regionIndexSet.has(index) && index !== activeRegionIndex
        cell.classList.toggle('size-table__table-header-cell--hidden', shouldHide)
      })

      rows.slice(1).forEach((row) => {
        const cells = Array.from(row.querySelectorAll('th, td'))
        cells.forEach((cell, index) => {
          const shouldHide = regionIndexSet.has(index) && index !== activeRegionIndex
          cell.classList.toggle('size-table__table-cell--hidden', shouldHide)
        })
      })
    })
  }

  onTableCellMouseEnter (event) {
    const cell = event.currentTarget
    const table = cell.closest('.size-table__table')

    if (!table) return
    if (
      cell.classList.contains('size-table__table-cell--hidden') ||
      cell.classList.contains('size-table__table-header-cell--hidden')
    ) {
      return
    }

    this.applyTableHover(table, cell)
  }

  onTableMouseLeave (event) {
    const table = event.currentTarget
    if (!table) return

    this.clearTableHover(table)
  }

  applyTableHover (table, hoveredCell) {
    this.clearTableHover(table)
    table.classList.add('size-table__table--hovering')

    const hoveredRow = hoveredCell.closest('tr')
    const hoveredColumnIndex = hoveredCell.cellIndex

    if (hoveredRow) {
      const visibleRowCells = Array.from(hoveredRow.children).filter((cell) => !this.isHiddenTableCell(cell))

      visibleRowCells.forEach((cell) => {
        cell.classList.add('size-table__table-cell--hover-row')
      })

      if (visibleRowCells[0]) {
        visibleRowCells[0].classList.add('size-table__table-cell--hover-row-start')
      }

      if (visibleRowCells.length > 0) {
        visibleRowCells[visibleRowCells.length - 1].classList.add('size-table__table-cell--hover-row-end')
      }
    }

    const visibleColumnCells = []

    table.querySelectorAll('tr').forEach((row) => {
      const columnCell = row.children[hoveredColumnIndex]
      if (!columnCell) return
      if (this.isHiddenTableCell(columnCell)) return

      visibleColumnCells.push(columnCell)

      columnCell.classList.add('size-table__table-cell--hover-column')
    })

    if (visibleColumnCells[0]) {
      visibleColumnCells[0].classList.add('size-table__table-cell--hover-column-start')
    }

    if (visibleColumnCells.length > 0) {
      visibleColumnCells[visibleColumnCells.length - 1].classList.add('size-table__table-cell--hover-column-end')
    }

    hoveredCell.classList.add('size-table__table-cell--hovered')
  }

  clearTableHover (table) {
    table.classList.remove('size-table__table--hovering')

    table.querySelectorAll(
      '.size-table__table-cell--hover-row, .size-table__table-cell--hover-row-start, .size-table__table-cell--hover-row-end, .size-table__table-cell--hover-column, .size-table__table-cell--hover-column-start, .size-table__table-cell--hover-column-end, .size-table__table-cell--hovered',
    ).forEach((cell) => {
      cell.classList.remove(
        'size-table__table-cell--hover-row',
        'size-table__table-cell--hover-row-start',
        'size-table__table-cell--hover-row-end',
        'size-table__table-cell--hover-column',
        'size-table__table-cell--hover-column-start',
        'size-table__table-cell--hover-column-end',
        'size-table__table-cell--hovered',
      )
    })
  }

  isHiddenTableCell (cell) {
    return (
      cell.classList.contains('size-table__table-cell--hidden') ||
      cell.classList.contains('size-table__table-header-cell--hidden')
    )
  }

  getTable (key) {
    if (!this.els?.panel?.exists || !this.els?.table?.exists) return null

    const container = this.els.panel.elements.find((panel) => panel.dataset.tab === key)
    if (!container) return null

    return this.els.table.elements.find((table) => container.contains(table)) || null
  }

  getTableCells (table, selector) {
    if (!table) return []

    return Array.from(table.querySelectorAll(selector))
  }

  onSelectChange () {
    const value = this.els?.select?.element?.value
    if (!value) return

    this.updateHeadingsForTable(this.garmentTable, value)
    this.updateHeadingsForTable(this.bodyTable, value)

    this.headings = value
  }

  updateHeadingsForTable (table, newHeading) {
    const currentObject = this.conversions[this.headings]
    const newObject = this.conversions[newHeading]

    if (!currentObject || !newObject) return

    this.getTableCells(table, 'tr:first-child th, tr:first-child td').forEach((cell) => {
      if (cell.cellIndex === 0) return

      const currentValue = cell.dataset.value
      const valueIndex = currentObject.findIndex((item) => String(item) === String(currentValue))
      if (valueIndex === -1) return

      const newValue = newObject[valueIndex]
      if (!newValue) return

      cell.innerHTML = newValue
      cell.dataset.value = String(newValue)
    })
  }

  setInitialTab () {
    const activeTab = this.els?.trigger?.elements.find((trigger) => trigger.getAttribute('aria-pressed') === 'true')
    if (!activeTab) return

    this.setAttribute('data-tab', activeTab.dataset.tab)
  }

  handleInitialConversion () {
    const handleConversion = (cell) => {
      if (cell.cellIndex === 0 || cell.closest('tr').rowIndex === 0) return

      const centimetres = parseFloat(cell.innerHTML, 10)
      if (isNaN(centimetres)) return

      const inches = Math.round((centimetres / 2.54) * 10) / 10

      cell.dataset.centimetres = String(centimetres)
      cell.dataset.inches = String(inches)

      cell.innerHTML = this.conversion === 'inches' ? String(inches) : String(centimetres)
    }

    this.getTableCells(this.garmentTable, 'td').forEach(handleConversion)
    this.getTableCells(this.bodyTable, 'td').forEach(handleConversion)
  }

  handleInitialHeadings () {
    const setHeadingCell = (cell) => {
      if (cell.cellIndex === 0) return

      const value = cell.innerHTML
      cell.dataset.value = value
    }

    this.getTableCells(this.garmentTable, 'tr:first-child th, tr:first-child td').forEach(setHeadingCell)
    this.getTableCells(this.bodyTable, 'tr:first-child th, tr:first-child td').forEach(setHeadingCell)
  }

  normaliseConversion (conversion) {
    const normalized = String(conversion || '').trim().toLowerCase()

    if (['in', 'inch', 'inches'].includes(normalized)) return 'inches'
    if (['cm', 'centimetres', 'centimeters', 'centimetres'].includes(normalized)) return 'centimetres'

    const derivedConversion = this.getConversionFromVisibleMeasurementsTable()
    if (derivedConversion) return derivedConversion

    return 'centimetres'
  }

  getConversionFromVisibleMeasurementsTable () {
    if (!this.els?.measurementsTable?.exists) return null

    const visibleTable = this.els.measurementsTable.elements.find((tableEl) => tableEl.getAttribute('aria-hidden') === 'false')
    if (!visibleTable) return null

    if (visibleTable.dataset.unit === 'inch') return 'inches'
    if (visibleTable.dataset.unit === 'cm') return 'centimetres'

    return null
  }

  getMeasurementsUnitFromConversion (conversion) {
    return this.isInchesConversion(conversion) ? 'inch' : 'cm'
  }

  isInchesConversion (conversion) {
    return this.normaliseConversion(conversion) === 'inches'
  }

  async onInchesClick () {
    this.convertToInches()
    await this.persistConversion('inches')
  }

  async onCentimetresClick () {
    this.convertToCentimetres()
    await this.persistConversion('centimetres')
  }

  async persistConversion (value) {
    await this.persistAttribute(this.storageName, value)
  }

  async persistBodyRegion (value) {
    await this.persistAttribute(this.regionStorageName, value)
  }

  async persistAttribute (key, value) {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          [key]: value,
        },
      }),
    })
  }

  convertToInches () {
    if (this.conversion === 'inches') return

    this.conversion = 'inches'

    if (this.els?.inches?.exists) {
      this.els.inches.elements.forEach((el) => el.setAttribute('aria-current', true))
    }

    if (this.els?.centimetres?.exists) {
      this.els.centimetres.elements.forEach((el) => el.setAttribute('aria-current', false))
    }

    this.getTableCells(this.garmentTable, 'td').forEach((cell) => {
      if (!cell.dataset.inches) return
      cell.innerHTML = cell.dataset.inches
    })

    this.getTableCells(this.bodyTable, 'td').forEach((cell) => {
      if (!cell.dataset.inches) return
      cell.innerHTML = cell.dataset.inches
    })
  }

  convertToCentimetres () {
    if (this.conversion === 'centimetres') return

    this.conversion = 'centimetres'

    if (this.els?.inches?.exists) {
      this.els.inches.elements.forEach((el) => el.setAttribute('aria-current', false))
    }

    if (this.els?.centimetres?.exists) {
      this.els.centimetres.elements.forEach((el) => el.setAttribute('aria-current', true))
    }

    this.getTableCells(this.garmentTable, 'td').forEach((cell) => {
      if (!cell.dataset.centimetres) return
      cell.innerHTML = cell.dataset.centimetres
    })

    this.getTableCells(this.bodyTable, 'td').forEach((cell) => {
      if (!cell.dataset.centimetres) return
      cell.innerHTML = cell.dataset.centimetres
    })
  }

  handleTrigger (event) {
    const triggerEl = event.target.closest(this.els?.trigger?.selector)
    if (!triggerEl) return

    if (this.els?.panel?.exists) {
      this.els.panel.elements.forEach((panelEl) => {
        panelEl.setAttribute('aria-hidden', panelEl.dataset.tab !== triggerEl.dataset.tab)
      })
    }

    if (this.els?.trigger?.exists) {
      this.els.trigger.elements.forEach((trigger) => {
        trigger.setAttribute('aria-pressed', trigger.dataset.tab === triggerEl.dataset.tab)
      })
    }

    this.setAttribute('data-tab', triggerEl.dataset.tab)
  }

  onFitUpdate (event) {
    if (!event?.detail) return
    if (String(event.detail.sectionId) !== String(this.sectionId)) return

    this.updateFittingUI(event.detail.fittingInfo)
  }

  updateFittingUI (fittingInfo) {
    this.fittingInfo = fittingInfo || ''

    this.updateTriggerFitText(this.fittingInfo)
    this.updateModalFitIndicator(this.fittingInfo)
  }

  updateTriggerFitText (fittingInfo) {
    if (!this.sectionId) return

    const sectionRoot = document.querySelector(`[data-size-guide-section="${this.sectionId}"]`)
    if (!sectionRoot) return

    const triggerFitEl = sectionRoot.querySelector('[data-size-guide-el="trigger-fit"]')
    if (!triggerFitEl) return

    const normalizedValue = String(fittingInfo || '').trim().toLowerCase()

    if (!normalizedValue) {
      triggerFitEl.classList.add('hidden')
      triggerFitEl.textContent = ''
      return
    }

    const template = triggerFitEl.dataset.fitTemplate || ''
    if (template.includes('[fitting_info]')) {
      triggerFitEl.textContent = template.replace('[fitting_info]', normalizedValue)
    } else {
      triggerFitEl.textContent = normalizedValue
    }

    triggerFitEl.classList.remove('hidden')
  }

  updateModalFitIndicator (fittingInfo) {
    const fittingBlock = this.els?.fitting?.element
    if (!fittingBlock) return

    const normalizedValue = this.normalizeFittingValue(fittingInfo)

    if (!normalizedValue) {
      fittingBlock.classList.add('hidden')
      return
    }

    fittingBlock.classList.remove('hidden')

    if (!this.els?.['fit-label']?.exists) return

    this.els['fit-label'].elements.forEach((label) => {
      const labelValue = this.normalizeFittingValue(label.dataset.fitValue)
      label.classList.toggle('prd-SizeGuide_FittingBarLabel-active', labelValue === normalizedValue)
    })
  }

  normalizeFittingValue (value) {
    return String(value || '').trim().toLowerCase()
  }

  toHandle (value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  getConfiguredBodyRegions () {
    return String(this.dataset.bodyRegions || '')
      .split('||')
      .map((value) => value.trim())
      .filter(Boolean)
  }

  getMatchingBodyRegion (value) {
    const nextRegionHandle = this.toHandle(value)
    if (!nextRegionHandle) return ''

    const match = this.bodyRegions.find((region) => this.toHandle(region) === nextRegionHandle)
    return match || ''
  }

  get storageName () {
    return 'size_guide_conversion'
  }

  get regionStorageName () {
    return 'size_guide_region'
  }

  get conversions () {
    return {
      UK: ['UK 6', 'UK 8', 'UK 10', 'UK 12', 'UK 14', 'UK 16', 'UK 18', 'UK 20'],
      US: ['2', '4', '6', '8', '10', '12', '14', '16'],
      Italian: ['38', '40', '42', '44', '46', '48', '50', '52'],
      French: ['34', '36', '38', '40', '42', '44', '46', '48'],
      Japan: ['5', '7', '9', '11', '13', '15', '17', '19'],
      Denmark: ['32', '34', '36', '38', '40', '42', '44', '46'],
      Universal: ['XS', 'XS-S', 'S-M', 'M-L', 'L-XL', 'XL', 'XL', 'XXL'],
    }
  }
}

if (!customElements.get('size-guide')) {
  customElements.define('size-guide', SizeGuide)
}
